import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  FileText,
  Download,
  Pen,
  CheckCircle,
  Package,
  RefreshCw,
  Eye,
  Shield,
  X,
  Search,
  Upload,
  ChevronRight,
  SortAsc,
  Filter,
  CheckSquare,
  Square,
  Printer,
  ChevronDown,
  Plus,
  BarChart3,
  FileUp,
} from 'lucide-react';
import DocumentEngine, { DOC_STATUS, DOC_TYPE } from '../../services/documents/DocumentEngine';
import { Modal, EmptyState, Tooltip } from '../Common/ReusableUI';
import { ListSkeleton } from '../Common/SkeletonLoaders';
import { useToast } from '../Common/ToastProvider';

// ── Configuration ──────────────────────────────────────────────────────

const STATUS_CONFIG = {
  [DOC_STATUS.DRAFT]: { label: 'Draft', color: 'text-slate-600', bg: 'bg-slate-100', icon: FileText },
  [DOC_STATUS.GENERATED]: { label: 'Generated', color: 'text-blue-600', bg: 'bg-blue-100', icon: FileText },
  [DOC_STATUS.PENDING_SIGNATURE]: { label: 'Awaiting Signature', color: 'text-amber-600', bg: 'bg-amber-100', icon: Pen },
  [DOC_STATUS.SIGNED]: { label: 'Signed', color: 'text-emerald-600', bg: 'bg-emerald-100', icon: CheckCircle },
  [DOC_STATUS.ARCHIVED]: { label: 'Archived', color: 'text-slate-500', bg: 'bg-slate-50', icon: Package },
};

const STATUS_TOOLTIPS = {
  [DOC_STATUS.DRAFT]: 'Document is in draft — not yet finalised',
  [DOC_STATUS.GENERATED]: 'Document generated and ready for signatures',
  [DOC_STATUS.PENDING_SIGNATURE]: 'One or more signatures are still required',
  [DOC_STATUS.SIGNED]: 'All required parties have signed',
  [DOC_STATUS.ARCHIVED]: 'Document has been archived',
};

const CATEGORY_MAP = {
  [DOC_TYPE.RESIDENT_AGREEMENT]: 'Admission',
  [DOC_TYPE.ACCOMMODATION_AGREEMENT]: 'Admission',
  [DOC_TYPE.ADMISSION_CHECKLIST]: 'Admission',
  [DOC_TYPE.CARE_PLAN]: 'Clinical',
  [DOC_TYPE.CONSENT_MEDICAL]: 'Clinical',
  [DOC_TYPE.CONSENT_MY_HEALTH_RECORD]: 'Clinical',
  [DOC_TYPE.STATEMENT_OF_RIGHTS]: 'Legal',
  [DOC_TYPE.CONSENT_PHOTO]: 'Legal',
  [DOC_TYPE.CONSENT_DATA_SHARING]: 'Legal',
  [DOC_TYPE.FEE_SCHEDULE]: 'Financial',
  [DOC_TYPE.MONTHLY_STATEMENT]: 'Financial',
};

const CATEGORY_COLORS = {
  Admission: { bg: 'bg-sky-50', text: 'text-sky-700', dot: 'bg-sky-500', chipActive: 'bg-sky-600', tag: 'bg-sky-100 text-sky-700' },
  Clinical: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', chipActive: 'bg-emerald-600', tag: 'bg-emerald-100 text-emerald-700' },
  Legal: { bg: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-500', chipActive: 'bg-violet-600', tag: 'bg-violet-100 text-violet-700' },
  Financial: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500', chipActive: 'bg-amber-600', tag: 'bg-amber-100 text-amber-700' },
  Other: { bg: 'bg-slate-50', text: 'text-slate-600', dot: 'bg-slate-400', chipActive: 'bg-slate-600', tag: 'bg-slate-100 text-slate-600' },
};

const CATEGORIES = ['All', 'Admission', 'Clinical', 'Legal', 'Financial', 'Other'];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name', label: 'Name A-Z' },
  { value: 'status', label: 'Status' },
];

const STATUS_SORT_ORDER = {
  [DOC_STATUS.PENDING_SIGNATURE]: 0,
  [DOC_STATUS.GENERATED]: 1,
  [DOC_STATUS.DRAFT]: 2,
  [DOC_STATUS.SIGNED]: 3,
  [DOC_STATUS.ARCHIVED]: 4,
};

const ACCEPTED_FILE_TYPES = '.pdf,.doc,.docx,.png,.jpg,.jpeg,.tiff';

function getCategory(docType) {
  return CATEGORY_MAP[docType] || 'Other';
}

function relativeTime(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ── Signature Capture ──────────────────────────────────────────────────

function SignatureCapture({ onSign, onCancel, signerRole }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [signerName, setSignerName] = useState('');

  const startDrawing = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  }, []);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasContent(true);
  }, [isDrawing]);

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasContent(false);
  }, []);

  const handleSign = useCallback(() => {
    if (!hasContent || !signerName.trim()) return;
    const canvas = canvasRef.current;
    const signatureData = canvas?.toDataURL('image/png') || 'signature_captured';
    onSign({ signatureData, signedBy: signerName.trim() });
  }, [hasContent, signerName, onSign]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-lg">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-900">Sign as: {signerRole}</h4>
        <button type="button" onClick={onCancel} className="rounded-md p-1 text-slate-400 hover:text-slate-600">
          <X className="h-4 w-4" />
        </button>
      </div>

      <input
        type="text"
        placeholder="Full name"
        value={signerName}
        onChange={(e) => setSignerName(e.target.value)}
        className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-100"
      />

      <div className="relative mb-3 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50">
        <canvas
          ref={canvasRef}
          width={360}
          height={120}
          className="w-full cursor-crosshair touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasContent && (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-slate-400">
            Draw signature here
          </p>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={clearCanvas}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={handleSign}
          disabled={!hasContent || !signerName.trim()}
          className="rounded-lg bg-purple-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Apply Signature
        </button>
      </div>
    </div>
  );
}

// ── Progress Stats ─────────────────────────────────────────────────────

function ProgressStats({ documents }) {
  const stats = useMemo(() => {
    const total = documents.length;
    const signed = documents.filter((d) => d.status === DOC_STATUS.SIGNED).length;
    const pending = documents.filter((d) => d.status === DOC_STATUS.PENDING_SIGNATURE).length;
    const generated = documents.filter((d) => d.status === DOC_STATUS.GENERATED).length;
    const pct = total ? Math.round((signed / total) * 100) : 0;
    return { total, signed, pending, generated, pct };
  }, [documents]);

  if (stats.total === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100">
            <BarChart3 className="h-4 w-4 text-purple-600" />
          </div>
          <div>
            <p className="text-lg font-bold text-slate-900 leading-tight">{stats.total}</p>
            <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Total</p>
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
          </div>
          <div>
            <p className="text-lg font-bold text-emerald-700 leading-tight">{stats.signed}</p>
            <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Signed</p>
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100">
            <Pen className="h-4 w-4 text-amber-600" />
          </div>
          <div>
            <p className="text-lg font-bold text-amber-700 leading-tight">{stats.pending}</p>
            <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Pending</p>
          </div>
        </div>
      </div>
      <div className="col-span-2 sm:col-span-1 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
            <FileText className="h-4 w-4 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-lg font-bold text-purple-700 leading-tight">{stats.pct}%</p>
            <div className="mt-0.5 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-purple-500 transition-all duration-500"
                style={{ width: `${stats.pct}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Generate Document Dropdown ─────────────────────────────────────────

function GenerateDocDropdown({ templates, onGenerate }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Tooltip content="Generate individual document">
        <button
          type="button"
          onClick={() => setIsOpen((p) => !p)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">New Doc</span>
          <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </Tooltip>
      {isOpen && (
        <div className="absolute right-0 z-30 mt-1.5 w-72 rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl">
          <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Select template</p>
          <div className="max-h-64 overflow-y-auto">
            {templates.map((t) => {
              const cat = getCategory(t.type);
              const catColor = CATEGORY_COLORS[cat] || CATEGORY_COLORS.Other;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { onGenerate(t.id); setIsOpen(false); }}
                  className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${catColor.dot}`} />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{t.name}</p>
                    <p className="text-[10px] text-slate-500 line-clamp-1">{t.description}</p>
                  </div>
                  <span className={`mt-0.5 shrink-0 rounded-full px-1.5 py-px text-[9px] font-bold ${catColor.tag}`}>{cat}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────

export default function DocumentManager({ residentId, facilityId, residentData = {} }) {
  const { showToast } = useToast();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingDoc, setSigningDoc] = useState(null);
  const [signingRole, setSigningRole] = useState(null);
  const [auditTrail, setAuditTrail] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState([]);

  const searchInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const residentName = residentData?.residentName
    || (residentData?.firstName && `${residentData.firstName} ${residentData.lastName || ''}`.trim())
    || 'Resident';

  // ── Keyboard shortcuts ──────────────────────────────────────────────

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape' && searchQuery) {
        setSearchQuery('');
        searchInputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [searchQuery]);

  // ── Data loading ────────────────────────────────────────────────────

  const refresh = useCallback(() => {
    const docs = DocumentEngine.getDocuments({ residentId });
    setDocuments(docs);
    setLoading(false);
  }, [residentId]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => refresh(), 300);
    return () => clearTimeout(timer);
  }, [refresh]);

  // ── Filtered & sorted list ──────────────────────────────────────────

  const filteredDocs = useMemo(() => {
    let result = [...documents];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((d) =>
        d.templateName?.toLowerCase().includes(q)
        || d.type?.toLowerCase().includes(q)
        || getCategory(d.type).toLowerCase().includes(q)
      );
    }

    if (activeCategory !== 'All') {
      result = result.filter((d) => getCategory(d.type) === activeCategory);
    }

    switch (sortBy) {
      case 'oldest':
        result.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case 'name':
        result.sort((a, b) => (a.templateName || '').localeCompare(b.templateName || ''));
        break;
      case 'status':
        result.sort((a, b) => (STATUS_SORT_ORDER[a.status] ?? 9) - (STATUS_SORT_ORDER[b.status] ?? 9));
        break;
      default:
        result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    return result;
  }, [documents, searchQuery, activeCategory, sortBy]);

  // ── Actions ─────────────────────────────────────────────────────────

  const handleGeneratePack = useCallback(() => {
    DocumentEngine.generateAdmissionPack(residentId, facilityId, residentData, 'current_user');
    refresh();
    showToast('Admission pack generated — all documents ready', 'success');
  }, [residentId, facilityId, residentData, refresh, showToast]);

  const handleGenerateDoc = useCallback((templateId) => {
    const result = DocumentEngine.generateDocument({ templateId, residentId, facilityId, residentData, createdBy: 'current_user' });
    refresh();
    if (result.ok) {
      showToast(`"${result.document.templateName}" created`, 'success');
    } else {
      showToast('Failed to generate document', 'error');
    }
  }, [residentId, facilityId, residentData, refresh, showToast]);

  const handleExportPDF = useCallback((docId) => {
    const html = DocumentEngine.exportAsPrintableHTML(docId);
    if (!html) return;
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
    showToast('Document exported for printing', 'info');
  }, [showToast]);

  const handlePreview = useCallback((doc) => {
    const html = DocumentEngine.exportAsPrintableHTML(doc.id);
    if (html) {
      setPreviewDoc({ ...doc, html });
    }
  }, []);

  const handlePrintPreview = useCallback(() => {
    if (!previewDoc?.html) return;
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(previewDoc.html);
      win.document.close();
      setTimeout(() => win.print(), 400);
    }
    showToast('Print dialog opened', 'info');
  }, [previewDoc, showToast]);

  const handleStartSigning = useCallback((doc, role) => {
    setSigningDoc(doc);
    setSigningRole(role);
  }, []);

  const handleSign = useCallback(({ signatureData, signedBy }) => {
    if (!signingDoc || !signingRole) return;

    DocumentEngine.addSignature(signingDoc.id, {
      role: signingRole,
      signedBy,
      signatureData,
    });

    setSigningDoc(null);
    setSigningRole(null);
    refresh();
    showToast(`Signed by ${signedBy} as ${signingRole.replace(/_/g, ' ')}`, 'success');
  }, [signingDoc, signingRole, refresh, showToast]);

  const handleViewAudit = useCallback((docId) => {
    const trail = DocumentEngine.getSignatureAuditTrail(docId);
    setAuditTrail({ docId, entries: trail });
  }, []);

  // ── Bulk selection ──────────────────────────────────────────────────

  const toggleSelect = useCallback((docId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredDocs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredDocs.map((d) => d.id)));
    }
  }, [filteredDocs, selectedIds.size]);

  const handleBulkExport = useCallback(() => {
    let exported = 0;
    selectedIds.forEach((id) => {
      const html = DocumentEngine.exportAsPrintableHTML(id);
      if (html) {
        const win = window.open('', '_blank');
        if (win) { win.document.write(html); win.document.close(); exported++; }
      }
    });
    showToast(`Exported ${exported} document${exported !== 1 ? 's' : ''}`, 'success');
    setSelectedIds(new Set());
  }, [selectedIds, showToast]);

  const handleBulkSign = useCallback(() => {
    showToast(`Bulk signing initiated for ${selectedIds.size} document(s)`, 'info');
    const firstPendingId = [...selectedIds].find((id) => {
      const doc = documents.find((d) => d.id === id);
      return doc && (doc.requiredSignatures || []).some((rs) => !rs.signedAt);
    });
    if (firstPendingId) {
      const doc = documents.find((d) => d.id === firstPendingId);
      const unsignedRole = doc.requiredSignatures.find((rs) => !rs.signedAt);
      if (unsignedRole) handleStartSigning(doc, unsignedRole.role);
    }
  }, [selectedIds, documents, showToast, handleStartSigning]);

  // ── Drag-and-drop + file input ──────────────────────────────────────

  const processFiles = useCallback((files) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);
    const incoming = fileList.map((f) => ({
      id: `upload_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name: f.name,
      size: f.size,
      type: f.type,
      progress: 0,
    }));
    setUploadingFiles((prev) => [...prev, ...incoming]);

    const names = fileList.map((f) => f.name).join(', ');
    showToast(`Uploading ${fileList.length} file(s): ${names}`, 'info');
    console.debug('[DocumentManager] Files received:', fileList.map((f) => ({ name: f.name, size: f.size, type: f.type })));

    incoming.forEach((file) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 30 + 10;
        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);
          setTimeout(() => {
            setUploadingFiles((prev) => prev.filter((f) => f.id !== file.id));
            showToast(`"${file.name}" uploaded successfully`, 'success');
          }, 500);
        }
        setUploadingFiles((prev) =>
          prev.map((f) => (f.id === file.id ? { ...f, progress: Math.min(100, Math.round(progress)) } : f))
        );
      }, 400);
    });
  }, [showToast]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    processFiles(e.dataTransfer.files);
  }, [processFiles]);

  const handleFileInputChange = useCallback((e) => {
    processFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [processFiles]);

  const templates = DocumentEngine.getTemplates();

  // ── Render ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-purple-600" />
          <h3 className="text-base font-bold text-slate-900">Documents &amp; Agreements</h3>
        </div>
        <ListSkeleton count={4} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1.5 text-xs text-slate-500" aria-label="Breadcrumb">
        <FileText className="h-3.5 w-3.5 text-purple-500" />
        <span className="font-medium text-slate-600">Documents</span>
        <ChevronRight className="h-3 w-3" />
        <span className="font-semibold text-slate-900 truncate max-w-[200px]">{residentName}</span>
        {documents.length > 0 && (
          <>
            <span className="text-slate-300 mx-0.5">·</span>
            <span className="text-slate-400">{documents.length} doc{documents.length !== 1 ? 's' : ''}</span>
          </>
        )}
      </nav>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-purple-100">
            <FileText className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900 leading-tight">Documents &amp; Agreements</h3>
            <p className="text-[11px] text-slate-500">Manage admission docs, consents &amp; signatures</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <GenerateDocDropdown templates={templates} onGenerate={handleGenerateDoc} />
          <Tooltip content="Generate all admission documents at once">
            <button
              type="button"
              onClick={handleGeneratePack}
              className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-purple-700 transition-colors"
            >
              <Package className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Admission Pack</span>
            </button>
          </Tooltip>
          <Tooltip content="Refresh document list">
            <button
              type="button"
              onClick={() => { setLoading(true); setTimeout(refresh, 200); }}
              className="inline-flex items-center justify-center rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Progress Stats */}
      <ProgressStats documents={documents} />

      {/* Drag-and-Drop Upload Zone */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_FILE_TYPES}
        onChange={handleFileInputChange}
        className="hidden"
        aria-hidden="true"
      />
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
        className={`group rounded-xl border-2 border-dashed p-5 text-center transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-200 ${
          isDragOver
            ? 'border-purple-400 bg-purple-50 shadow-inner scale-[1.01]'
            : 'border-slate-300 bg-slate-50/50 hover:border-purple-300 hover:bg-purple-50/30'
        }`}
      >
        <div className={`mx-auto flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
          isDragOver ? 'bg-purple-100' : 'bg-slate-100 group-hover:bg-purple-100'
        }`}>
          <Upload className={`h-5 w-5 transition-colors ${isDragOver ? 'text-purple-600' : 'text-slate-400 group-hover:text-purple-500'}`} />
        </div>
        <p className={`mt-2 text-sm font-medium transition-colors ${isDragOver ? 'text-purple-700' : 'text-slate-600'}`}>
          {isDragOver ? 'Release to upload' : 'Drop files here or click to browse'}
        </p>
        <p className="text-[11px] text-slate-400 mt-0.5">PDF, DOCX, or image files accepted</p>
      </div>

      {/* Upload Progress Indicators */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          {uploadingFiles.map((file) => (
            <div key={file.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
              <FileUp className="h-4 w-4 text-purple-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-700 truncate">{file.name}</p>
                <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-purple-500 transition-all duration-300"
                    style={{ width: `${file.progress}%` }}
                  />
                </div>
              </div>
              <span className="text-[10px] font-bold text-purple-600 shrink-0">{file.progress}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Search + Sort Bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search documents…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-16 text-sm text-slate-700 placeholder:text-slate-400 focus:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-100 transition-shadow"
          />
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="rounded p-0.5 text-slate-400 hover:text-slate-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : (
              <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] font-semibold text-slate-400">
                Ctrl+K
              </kbd>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <SortAsc className="h-4 w-4 text-slate-400 hidden sm:block" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 focus:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-100"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Category Filter Chips */}
      <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Document categories">
        <Filter className="h-3.5 w-3.5 text-slate-400 mr-0.5" />
        {CATEGORIES.map((cat) => {
          const isActive = activeCategory === cat;
          const count = cat === 'All'
            ? documents.length
            : documents.filter((d) => getCategory(d.type) === cat).length;
          const catColor = CATEGORY_COLORS[cat];
          return (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveCategory(cat)}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-all ${
                isActive
                  ? `${cat === 'All' ? 'bg-purple-600' : catColor?.chipActive || 'bg-purple-600'} text-white shadow-sm`
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat !== 'All' && (
                <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-white/60' : catColor?.dot || 'bg-slate-400'}`} />
              )}
              {cat}
              {count > 0 && (
                <span className={`rounded-full px-1.5 py-px text-[10px] font-bold ${
                  isActive ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Signature Capture Modal */}
      {signingDoc && signingRole && (
        <SignatureCapture
          signerRole={signingRole}
          onSign={handleSign}
          onCancel={() => { setSigningDoc(null); setSigningRole(null); }}
        />
      )}

      {/* Audit Trail Panel */}
      {auditTrail && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-purple-600" />
              <h4 className="text-sm font-bold text-slate-900">Signature Audit Trail</h4>
            </div>
            <button type="button" onClick={() => setAuditTrail(null)} className="rounded-md p-1 text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          {auditTrail.entries.length === 0 ? (
            <p className="text-xs text-slate-500">No signatures recorded</p>
          ) : (
            <div className="space-y-2">
              {auditTrail.entries.map((e) => (
                <div key={e.id} className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2 text-xs">
                  <CheckCircle className="h-4 w-4 shrink-0 text-emerald-600" />
                  <div className="flex-1">
                    <span className="font-semibold text-slate-700">{e.role}</span>
                    {' — '}{e.signedBy || 'Unknown'} at {new Date(e.signedAt).toLocaleString()}
                  </div>
                  <span className="text-[10px] text-slate-400">{relativeTime(e.signedAt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {documents.length === 0 && (
        <EmptyState
          icon={FileText}
          title="No documents generated yet"
          description="Generate individual documents or the full admission pack to get started."
          action={
            <div className="flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={handleGeneratePack}
                className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 transition-colors"
              >
                <Package className="h-4 w-4" />
                Generate Admission Pack
              </button>
              <div className="flex flex-wrap justify-center gap-2 mt-1">
                {templates.slice(0, 4).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => handleGenerateDoc(t.id)}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm hover:border-purple-300 hover:text-purple-700 transition-colors"
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          }
        />
      )}

      {/* No results after filtering */}
      {documents.length > 0 && filteredDocs.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 py-10 text-center">
          <Search className="mx-auto h-8 w-8 text-slate-300 mb-2" />
          <p className="text-sm font-medium text-slate-600">No documents match your filters</p>
          <p className="text-xs text-slate-400 mt-1">Try adjusting the search term or category</p>
          <button
            type="button"
            onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}
            className="mt-3 text-xs font-semibold text-purple-600 hover:text-purple-700"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Bulk select header */}
      {filteredDocs.length > 0 && (
        <div className="flex items-center justify-between px-1">
          <button
            type="button"
            onClick={toggleSelectAll}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
          >
            {selectedIds.size === filteredDocs.length && filteredDocs.length > 0 ? (
              <CheckSquare className="h-4 w-4 text-purple-600" />
            ) : (
              <Square className="h-4 w-4" />
            )}
            {selectedIds.size === filteredDocs.length && filteredDocs.length > 0 ? 'Deselect all' : 'Select all'}
          </button>
          <p className="text-[11px] text-slate-400">
            {filteredDocs.length} document{filteredDocs.length !== 1 ? 's' : ''}
            {activeCategory !== 'All' && ` in ${activeCategory}`}
          </p>
        </div>
      )}

      {/* Document List */}
      {filteredDocs.length > 0 && (
        <div className="space-y-2">
          {filteredDocs.map((doc, idx) => {
            const cfg = STATUS_CONFIG[doc.status] || STATUS_CONFIG.draft;
            const StatusIcon = cfg.icon;
            const unsignedRoles = (doc.requiredSignatures || []).filter((rs) => !rs.signedAt);
            const totalSigs = (doc.requiredSignatures || []).length;
            const signedSigs = totalSigs - unsignedRoles.length;
            const isSelected = selectedIds.has(doc.id);
            const category = getCategory(doc.type);
            const catColor = CATEGORY_COLORS[category] || CATEGORY_COLORS.Other;

            return (
              <div
                key={doc.id}
                className={`group rounded-xl border bg-white p-4 shadow-sm hover:shadow-md transition-all duration-200 ${
                  isSelected ? 'border-purple-300 ring-1 ring-purple-200 bg-purple-50/30' : 'border-slate-200'
                }`}
                style={{ animationDelay: `${idx * 30}ms` }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    {/* Checkbox */}
                    <button
                      type="button"
                      onClick={() => toggleSelect(doc.id)}
                      className="mt-0.5 shrink-0 text-slate-400 hover:text-purple-600 transition-colors"
                      aria-label={isSelected ? 'Deselect document' : 'Select document'}
                    >
                      {isSelected ? (
                        <CheckSquare className="h-5 w-5 text-purple-600" />
                      ) : (
                        <Square className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </button>

                    <div className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${cfg.bg}`}>
                      <StatusIcon className={`h-5 w-5 ${cfg.color}`} />
                      {/* Category color indicator */}
                      <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${catColor.dot}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{doc.templateName}</p>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
                        <span>v{doc.version}</span>
                        <span className="text-slate-300">·</span>
                        <Tooltip content={new Date(doc.createdAt).toLocaleString()}>
                          <span className="cursor-default">{relativeTime(doc.createdAt)}</span>
                        </Tooltip>
                        <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10px] font-semibold ${catColor.tag}`}>
                          <span className={`h-1 w-1 rounded-full ${catColor.dot}`} />
                          {category}
                        </span>
                      </div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <Tooltip content={STATUS_TOOLTIPS[doc.status] || cfg.label}>
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold cursor-default ${cfg.bg} ${cfg.color}`}>
                            <StatusIcon className="h-2.5 w-2.5" />
                            {cfg.label}
                          </span>
                        </Tooltip>
                        {totalSigs > 0 && (
                          <Tooltip content={`${signedSigs} of ${totalSigs} signatures collected`}>
                            <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 cursor-default">
                              <Pen className="h-2.5 w-2.5" />
                              {signedSigs}/{totalSigs} signed
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-0.5">
                    <Tooltip content="Preview document">
                      <button
                        type="button"
                        onClick={() => handlePreview(doc)}
                        className="rounded-lg p-2 text-slate-400 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Export as PDF">
                      <button
                        type="button"
                        onClick={() => handleExportPDF(doc.id)}
                        className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Signature audit trail">
                      <button
                        type="button"
                        onClick={() => handleViewAudit(doc.id)}
                        className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                      >
                        <Shield className="h-4 w-4" />
                      </button>
                    </Tooltip>
                  </div>
                </div>

                {/* Signature slots */}
                {unsignedRoles.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                    <span className="text-xs text-slate-500">Signatures needed:</span>
                    {unsignedRoles.map((rs) => (
                      <button
                        key={rs.role}
                        type="button"
                        onClick={() => handleStartSigning(doc, rs.role)}
                        className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100 hover:border-amber-300 transition-colors"
                      >
                        <Pen className="h-3 w-3" />
                        {rs.role.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                )}

                {/* Signed signatures */}
                {doc.signatures?.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {doc.signatures.map((s) => (
                      <Tooltip key={s.id} content={`Signed ${new Date(s.signedAt).toLocaleString()} (${relativeTime(s.signedAt)})`}>
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-medium text-emerald-700 cursor-default"
                        >
                          <CheckCircle className="h-3 w-3" />
                          {s.role}: {s.signedBy}
                        </span>
                      </Tooltip>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Floating Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 transform animate-[slideUp_0.2s_ease-out]">
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/95 backdrop-blur-md px-5 py-3 shadow-2xl ring-1 ring-black/5">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-purple-100">
              <span className="text-xs font-bold text-purple-700">{selectedIds.size}</span>
            </div>
            <span className="text-xs font-medium text-slate-600">selected</span>
            <div className="h-5 w-px bg-slate-200" />
            <Tooltip content="Export selected documents as PDF">
              <button
                type="button"
                onClick={handleBulkExport}
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 transition-colors"
              >
                <Download className="h-3.5 w-3.5" />
                Export
              </button>
            </Tooltip>
            <Tooltip content="Begin signing process for selected documents">
              <button
                type="button"
                onClick={handleBulkSign}
                className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-purple-700 transition-colors"
              >
                <Pen className="h-3.5 w-3.5" />
                Sign
              </button>
            </Tooltip>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="ml-1 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              aria-label="Clear selection"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Inline Preview Modal */}
      <Modal
        isOpen={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        title={previewDoc?.templateName || 'Document Preview'}
        size="lg"
      >
        {previewDoc && (
          <div>
            {/* Preview Toolbar */}
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2 rounded-t-lg -mt-2 -mx-1 mb-3">
              <div className="flex items-center gap-3 text-xs text-slate-600">
                <span className="font-medium">{previewDoc.templateName}</span>
                <span className="text-slate-300">|</span>
                <span>v{previewDoc.version}</span>
                <span className="text-slate-300">|</span>
                <span className={`${STATUS_CONFIG[previewDoc.status]?.color || 'text-slate-600'}`}>
                  {STATUS_CONFIG[previewDoc.status]?.label || previewDoc.status}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Tooltip content="Print document">
                  <button
                    type="button"
                    onClick={handlePrintPreview}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    <Printer className="h-3.5 w-3.5" />
                    Print
                  </button>
                </Tooltip>
                <Tooltip content="Export as PDF">
                  <button
                    type="button"
                    onClick={() => { handleExportPDF(previewDoc.id); }}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Export
                  </button>
                </Tooltip>
              </div>
            </div>
            {/* Preview Content */}
            {previewDoc.html && (
              <div className="max-h-[65vh] overflow-y-auto rounded-lg border border-slate-200">
                <iframe
                  title="Document Preview"
                  srcDoc={previewDoc.html}
                  className="w-full border-0"
                  style={{ minHeight: '600px' }}
                  sandbox="allow-same-origin"
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
